import {
    VerifiedAccessKeyResponse,
    VerifiedAccessKeyStatus,
} from "@biggeo/bg-server-lib/studio";
import {
    Box,
    Button,
    Divider,
    FlexScrollArea,
    FlexScrollAreaContainer,
    Grid,
    Stack,
    SubmittingButton,
    TextField,
    ThumbnailAvatar,
    Typography,
    textInputBaseClasses,
} from "@biggeo/bg-ui/lab";
import {
    CancelFilled,
    CheckCircle,
    CompareArrowsOutline,
} from "@biggeo/bg-ui/lab/icons";
import { values } from "lodash";
import { Link } from "react-router-dom";
import { isValid } from "zod";
import { Routes } from "../../navigation/redux/model";

interface IEnterAccessKeyForm {
    readonly apiKey: string;
    readonly response?: VerifiedAccessKeyResponse;
    readonly onChangeAccessKey: (v?: string) => void;
    readonly onUnlock: () => void;
    readonly loading: boolean;
}

const EnterAccessKeyForm = ({
    apiKey,
    response,
    onChangeAccessKey,
    loading,
    onUnlock,
}: IEnterAccessKeyForm) => {
    return (
        <FlexScrollAreaContainer height="100%">
            <FlexScrollArea
                sx={{
                    flexDirection: "column",
                    padding: 4,
                    minHeight: (theme) => theme.spacing(26),
                }}
                gap={4}
            >
                <Stack height="100%" gap={2} alignSelf="center">
                    <Grid
                        container
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                    >
                        <ThumbnailAvatar src={undefined} square size="md" />
                        <CompareArrowsOutline size="xs" color="disabled" />
                        <ThumbnailAvatar
                            src={
                                "https://sabiggeomedia01.blob.core.windows.net/media/bg-background.png"
                            }
                            square
                            size="md"
                        />
                    </Grid>
                    <Typography variant="h6" fontWeight="bold" align="center">
                        Unlock A{" "}
                        <Typography
                            as="span"
                            variant="h6"
                            fontWeight="bold"
                            color="primary"
                        >
                            BigGeo Indexed{" "}
                        </Typography>
                        Dataset
                    </Typography>
                    <Grid item sx={{ textAlign: "center" }}>
                        <Typography
                            variant="body3"
                            sx={{
                                display: "inline",
                            }}
                        >
                            A dataset access key grants access to a dataset
                            within your Datascape account. These access keys are
                            issued by BigGeo’s{" "}
                        </Typography>
                        <Link
                            to={`${Routes.bigGeoProductsDatalab}`}
                            target="_blank"
                        >
                            <Typography
                                variant="body3"
                                color="primary"
                                sx={{
                                    display: "inline",
                                }}
                            >
                                data provider partners.
                            </Typography>
                        </Link>
                    </Grid>
                    <TextField
                        value={apiKey}
                        placeholder="BG12-CD34-EF56-GH78-IJ90-KL12-MN34-OP56"
                        helperText={apiKey && response?.message}
                        sx={{
                            [`& .${textInputBaseClasses["input-root"]}`]: {
                                paddingX: 4,
                                paddingY:
                                    response?.status ===
                                    VerifiedAccessKeyStatus.Success
                                        ? 4
                                        : 2,
                                alignItems: "center",
                            },
                            [`& .${textInputBaseClasses.input}`]: {
                                minHeight: "23px",
                                maxHeight: "23px",
                                textTransform: "uppercase",
                            },
                        }}
                        slots={{
                            helperText: (props) => (
                                <Grid
                                    sx={{ display: "flex" }}
                                    alignItems="center"
                                    gap={1}
                                >
                                    {response?.status ===
                                    VerifiedAccessKeyStatus.Failed ? (
                                        <CancelFilled
                                            size="xxs"
                                            color={"error"}
                                        />
                                    ) : (
                                        <CheckCircle
                                            size="xxs"
                                            color={"success"}
                                        />
                                    )}

                                    <Typography
                                        {...props}
                                        variant="body4"
                                        sx={{
                                            color: (theme) =>
                                                theme.palette.disabled
                                                    .onContainer,
                                        }}
                                    />
                                </Grid>
                            ),
                        }}
                        fullWidth
                        onChange={(_, v) => {
                            onChangeAccessKey(v);
                        }}
                    />
                    <Grid
                        container
                        gap={1}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Link
                            to="https://datascape.featurebase.app/help/articles/5156192-dataset-access-key#6q5me7btlqe"
                            target="_blank"
                        >
                            <Button variant="minimal" tabIndex={-1}>
                                Forgot Access key?
                            </Button>
                        </Link>
                    </Grid>
                </Stack>
            </FlexScrollArea>
            <Stack
                sx={{
                    padding: 4,
                }}
            >
                <SubmittingButton
                    fullWidth
                    onClick={() => onUnlock()}
                    _type="button"
                    type="checking"
                    loading={loading}
                    disabled={!isValid}
                >
                    Unlock Access
                </SubmittingButton>
            </Stack>
        </FlexScrollAreaContainer>
    );
};
export default EnterAccessKeyForm;
